// react
import { Link } from 'react-router-dom';
// fullCalendar
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import jaLocale from '@fullcalendar/core/locales/ja';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from "@fullcalendar/interaction";
import { 
    EventDropArg,
    EventRemoveArg,
    EventClickArg,
    DatesSetArg
} from '@fullcalendar/core';
// react
import { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
// type
import { Schedule } from '../types/schedule';
// lib
import _ from 'lodash';
// component
import ConfirmDialog from './confirm-dialog';
import ScheduleControlDialog from './schedule-control-dialog';
// Util
import Utils from '../common/utils';
// api
import {
    fetchSchedules,
    upsertSchedule,
} from '../service/schedule-api';
// react material
import Button from '@mui/material/Button';

dayjs.extend(utc);
dayjs.extend(timezone);

const ScheduleControl = () => {

    const [ schedules, setSchedules ] = useState<Schedule[]>([]);
    const [ open, setOpen ] = useState<boolean>(false);
    const [ selectedDateTime, setSelectedDateTime ] = useState("");
    const [ targetSchedule, setTargetSchedule ] = useState<Schedule>();
    const [ isNew, setIsNew ] = useState(true);
    const [ isConfirm, setIsConfirm ] = useState(false);
    const [ confirmCallBack, setConfirmCallBack ] = useState<(confirmed: boolean) => void>(() => {});
    const [ confirmTitle, setConfirmTitle ] = useState("");
    const [ confirmMessage, setConfirmMessage ] = useState("");

    // 一度のみロード
    const hasFetched = useRef(false);

    useEffect(() => {
        // 初期表示時
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
            
            const view = calendarApi.view;
            const currentStart = view.currentStart;
            const { start, end } = Utils.getMonthViewGridRange(currentStart);
            const month = Utils.iosToYYYYMM(currentStart);

            // ローカルストレージに保存
            localStorage.setItem("month", month);
            
            if (!hasFetched.current) {
                const getSchedules = async () => {
                    const data = await fetchSchedules(start, end);
                    // 失敗した場合は処理終了
                    if (!data) {
                        return;
                    }
                    // 色を適用
                    setSchedules(Utils.applyColorsToSchedules(data));
                };
                // フラグを立てる
                hasFetched.current = true;
                // スケジュール表示
                getSchedules();
            }
        }
    }, [])

    /**
     * 🔵 予約の新規登録
     * @param info 
     */
    const calendarClick = (info: any) => {
        const clicked = dayjs(info.date).tz('Asia/Tokyo');

        // ★ 時刻が 00:00:00 なら 9時に変更
        const adjusted = clicked.hour() === 0 && clicked.minute() === 0
            ? clicked.hour(9)
            : clicked;

        const formatted = adjusted.format('YYYY-MM-DDTHH:mm:ssZ');

        setSelectedDateTime(formatted);
        setIsNew(true);
        setOpen(true);
    };

    /**
     * 🔵 インベトをクリックした時
     * @param eventClick 
     */
    const clickEvent = (eventClick: EventClickArg) => {
        
        const restoreSchedules = [...schedules];

        const filterSchedules: Schedule[] = _.filter(restoreSchedules, (schedule: Schedule) => {
            return schedule.id === eventClick.event.id;
        })
        const targetSchedule: Schedule = _.head(filterSchedules) as Schedule;
        setTargetSchedule(targetSchedule);
        setSelectedDateTime(eventClick.event.startStr);
        setIsNew(false);
        setOpen(true);
    }

    /**
     * 🔵 イベントをドラッグもしくはリサイズ
     * @param eventDrop 
     */
    const dropOrResizeEvent = (eventDrop: EventDropArg | EventRemoveArg) => {

        const eventDropEvent = eventDrop.event;
    
        const newSchedule: Schedule = {
            id: eventDropEvent.id,
            title: eventDropEvent.title,
            start: eventDropEvent.startStr,
            end: eventDropEvent.endStr,
            userId: eventDropEvent.extendedProps.userId,
        }

        const restoreSchedules = [...schedules];

        // 確認ダイアログ
        setConfirmCallBack(() => async (isConfirmed: boolean) => {
            if (isConfirmed) {
                // はい
                const data = await upsertSchedule(newSchedule);
                // 失敗した場合は処理終了
                if (!data) {
                    return;
                }
                const updateSchedules: Schedule[] = _.map(restoreSchedules, (schedule: Schedule) => {
                    return schedule.id === newSchedule.id ? newSchedule : schedule;
                });

                setSchedules(Utils.applyColorsToSchedules(updateSchedules));

            } else {
                // いいえ
                eventDrop.revert();
            }
            setIsConfirm(false);
        })
        setConfirmTitle("予約修正確認");
        setConfirmMessage("予約を修正しますか？");
        setIsConfirm(true);
    }

    /**
     * 🔵 カレンダーの日付を変更した時
     */
    const handleDateSet = async (arg: DatesSetArg) => {

        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
            const view = calendarApi.view;
            const currentStart = view.currentStart;
            const activeStart = view.activeStart;
            const activeEnd = view.activeEnd;

            // 月モード
            const isMonth = (view.type === "dayGridMonth");
            const month = Utils.iosToYYYYMM(currentStart);
            // ローカルストレージから復帰
            const localeStorageMonth = localStorage.getItem("month");
            // 月モードで、月が異なる場合はtrue
            const isMonthChange = (isMonth && localeStorageMonth !== month);

            // 週モードで、週頭と終わりの月が不一致で、月が変更の場合は
            const isMatchMonth = Utils.isMatchMonth(activeStart, activeEnd);
            const isWeekChange = (!isMonth && !isMatchMonth && localeStorageMonth !== month);

            if (isMonthChange || isWeekChange) {
                // ローカルストレージ
                localStorage.setItem("month", month);
                // リクエスト実施
                const { start, end } = Utils.getMonthViewGridRange(currentStart);
                const data = await fetchSchedules(start, end);
                // 失敗した場合は処理終了
                if (!data) {
                    return;
                }
                // カレンダーに反映
                setSchedules(Utils.applyColorsToSchedules(data));
            }
        }
    };

    const calendarRef = useRef<any>(null);

    // 初期表示位置
    const getInitialDate = (): string => {
        // 1. ダイアログ選択日があればそれを優先
        if (selectedDateTime) return selectedDateTime;

        // 2. ローカルストレージで前回表示日を保持していたら復元
        // const savedDate = localStorage.getItem("lastViewDate");
        // const savedDate = localStorage.getItem("lastViewDate");
        // if (savedDate) return savedDate;

        // 3. それ以外は現在の日付（初期表示）
        return new Date().toISOString();
    };

    return (
        <div>
            <FullCalendar 
                initialDate={getInitialDate()}
                height="100vh"
                key="calendar"
                plugins={[
                    dayGridPlugin, 
                    timeGridPlugin, 
                    listPlugin,
                    interactionPlugin, // ドラッグアンドドロップに対応
                ]} 
                initialView="timeGridWeek"
                locales={[jaLocale]}
                locale='ja'
                headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek listWeek',
                }}
                events={schedules}
                selectMirror={true} // ★カレンダー上で選択したものが可視化
                editable={true} // ◆ドラッグ&ドロップを有効化
                eventDrop={dropOrResizeEvent} // ◆ドラッグ&ドロップした時のイベント
                eventClick={clickEvent} // クリックしたイベントの情報を取得できる
                eventResize={dropOrResizeEvent} // リサイズした時のイベント
                slotMinTime="07:00:00" // カレンダー表示開始時刻
                slotMaxTime="23:00:00" // カレンダー表示終了時刻
                dateClick={(info) => calendarClick(info)}
                allDaySlot={false} // 終日イベント対応しない
                datesSet={handleDateSet} // カレンダーを変更した時
                ref={calendarRef} // カレンダーを変更した時
            />
            <ScheduleControlDialog
                open={open}
                setOpen={setOpen}
                isNew={isNew}
                targetSchedule={targetSchedule}
                setTargetSchedule={setTargetSchedule}
                selectedDateTime={selectedDateTime}
                selectedDisplayTime={Utils.iosToDateString(selectedDateTime)}
                schedules={schedules}
                setSchedules={setSchedules}
            />
            <ConfirmDialog
                open={isConfirm}
                onClose={() => { 
                    setIsConfirm(false); 
                    confirmCallBack(false)}
                }
                onConfirm={() => confirmCallBack(true)}
                title={confirmTitle}
                message={confirmMessage}
            />
            {/* ボタン */}
            <div className='nav-button-wrapper'>
                <Button className='nav-button' component={Link} to="/users" variant="contained" color="primary">
                    ユーザー
                </Button>
            </div>
        </div>
    )
}

export default ScheduleControl;